import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  TextField, 
  MenuItem, 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableRow, 
  TableContainer, 
  TablePagination,
  Button
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import DownloadIcon from '@mui/icons-material/Download';
import * as XLSX from 'xlsx';
import api from '../../api';

const AttendanceImportReport = () => {
  const [auditData, setAuditData] = useState([]);
  
  // Audit Filters & Pagination State
  const [auditSearch, setAuditSearch] = useState('');
  const [auditDept, setAuditDept] = useState('ALL');
  const [auditDesig, setAuditDesig] = useState('ALL');
  const [auditDate, setAuditDate] = useState('');
  const [auditPage, setAuditPage] = useState(0);
  const [auditRowsPerPage, setAuditRowsPerPage] = useState(10);

  useEffect(() => {
    fetchAuditData();
  }, []);

  const fetchAuditData = async () => {
    try {
      const res = await api.get('/attendance-import/audit-report');
      setAuditData(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  // Audit Filtering Logic
  const auditDepartments = ['ALL', ...new Set(auditData.map(item => item.department).filter(Boolean))];
  const auditDesignations = ['ALL', ...new Set(auditData.map(item => item.designation).filter(Boolean))];

  const filteredAuditData = auditData.filter(item => {
    const matchSearch = item.emp_id.toLowerCase().includes(auditSearch.toLowerCase()) || 
                        item.employee_name.toLowerCase().includes(auditSearch.toLowerCase());
    const matchDept = auditDept === 'ALL' || item.department === auditDept;
    const matchDesig = auditDesig === 'ALL' || item.designation === auditDesig;
    const matchDate = !auditDate || item.date === auditDate;
    
    return matchSearch && matchDept && matchDesig && matchDate;
  });

  const exportAuditToExcel = () => {
    if (filteredAuditData.length === 0) return;
    const worksheet = XLSX.utils.json_to_sheet(filteredAuditData.map(row => ({
      "Emp ID": row.emp_id,
      "Name": row.employee_name,
      "Department": row.department,
      "Designation": row.designation,
      "Date": row.date,
      "Shift Start": row.shift_start,
      "Shift End": row.shift_end,
      "Status": row.status,
      "Uploaded On": row.uploaded_on,
      "Uploaded By": row.imported_by
    })));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Import_Audit_Report");
    XLSX.writeFile(workbook, `Attendance_Import_Audit.xlsx`);
  };

  return (
    <Box sx={{ width: '100%' }}>

      {/* FILTER BAR FOR AUDIT REPORT */}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#ffffff', mb: 3, border: '1px solid #e0e0e0', display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField 
          size="small" 
          label="Search Emp ID / Name" 
          value={auditSearch} 
          onChange={(e) => { setAuditSearch(e.target.value); setAuditPage(0); }} 
          sx={{ minWidth: 200 }} 
        />
        
        <TextField
          select
          size="small"
          label="Department"
          value={auditDept}
          onChange={(e) => { setAuditDept(e.target.value); setAuditPage(0); }}
          sx={{ minWidth: 160 }}
        >
          {auditDepartments.map(dept => (
            <MenuItem key={dept} value={dept}>{dept}</MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="Designation"
          value={auditDesig}
          onChange={(e) => { setAuditDesig(e.target.value); setAuditPage(0); }}
          sx={{ minWidth: 160 }}
        >
          {auditDesignations.map(desig => (
            <MenuItem key={desig} value={desig}>{desig}</MenuItem>
          ))}
        </TextField>

        <TextField 
          size="small" 
          type="date"
          label="Date"
          InputLabelProps={{ shrink: true }}
          value={auditDate} 
          onChange={(e) => { setAuditDate(e.target.value); setAuditPage(0); }} 
          sx={{ width: 150 }} 
        />

        <Box sx={{ flexGrow: 1 }} />
        
        <Button variant="outlined" color="success" startIcon={<DownloadIcon />} onClick={exportAuditToExcel}>
          Export to Excel
        </Button>
      </Paper>

      {/* AUDIT DATA TABLE */}
      <Paper variant="outlined" sx={{ borderRadius: 2, bgcolor: '#ffffff', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Employee</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Department</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Shift</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Uploaded On</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Uploaded By</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAuditData.slice(auditPage * auditRowsPerPage, auditPage * auditRowsPerPage + auditRowsPerPage).map((row, idx) => (
                <TableRow key={idx} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">{row.emp_id}</Typography>
                    <Typography variant="caption">{row.employee_name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{row.department}</Typography>
                    <Typography variant="caption" color="textSecondary">{row.designation}</Typography>
                  </TableCell>
                  <TableCell>{row.date}</TableCell>
                  <TableCell>{row.shift_start} - {row.shift_end}</TableCell>
                  <TableCell>{row.status}</TableCell>
                  <TableCell>{row.uploaded_on}</TableCell>
                  <TableCell>{row.imported_by}</TableCell>
                </TableRow>
              ))}
              {filteredAuditData.length === 0 && (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3 }}>No matching imports found.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredAuditData.length}
          page={auditPage}
          onPageChange={(e, newPage) => setAuditPage(newPage)}
          rowsPerPage={auditRowsPerPage}
          onRowsPerPageChange={(e) => {
            setAuditRowsPerPage(parseInt(e.target.value, 10));
            setAuditPage(0);
          }}
          sx={{ borderTop: '1px solid #e0e0e0' }}
        />
      </Paper>
    </Box>
  );
};

export default AttendanceImportReport;